import { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { IconButton, MenuItem, Select, FormHelperText } from "@mui/material";
import {
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  XAxis,
  YAxis,
  Bar
} from "recharts";
import { fetchChecklistAnswerStreaksThunk } from "../../../store/thunk/reportingThunk";
import { useChecklistAnswerStreakData } from '../../../store/selectors/reportingSelectors';
import { useAppDispatch } from "../../../hooks/StoreHooks";
import {
  OptionContainer,
  StyledFormControl,
  ChartContainer,
  NoDataText,
  ActionRow,
  OptionRow
} from "./Widget.styles";
import { NoDataContainer } from "../Dashboard.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";

export type StreakChartDataType = {
  name: string;
  streak: number;
};

const takeOptions: number[] = [5, 10, 15, 20];

const ChecklistAnswerStreakChart = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const checklistAnswerStreakData = useChecklistAnswerStreakData();

  const [take, setTake] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [emptyChartData, setEmptyChartData] = useState<boolean>(true);

  const handleSelection = (setter) => (e) => setter(e.target.value);

  const fetchChartData = () => {
    setLoading(false);
    dispatch(fetchChecklistAnswerStreaksThunk())
      .then(() => {
        setLoading(true);
        setEmptyChartData(false);
      });
  };

  const resetChart = () => {
    setEmptyChartData(true);
  };

  const chartData: StreakChartDataType[] = useMemo(() => (
    emptyChartData ?
      [{ name: "", streak: 0 }] :
      (checklistAnswerStreakData ?? [])
        .sort((a, b) => b.streak - a.streak)
        .slice(0, take)
        .map((item) => ({
          name: item.actionModuleName,
          streak: item.streak
        }))
  ), [checklistAnswerStreakData, take, emptyChartData]);

  return (
    <>
      <OptionContainer>
        <OptionRow>
          <StyledFormControl>
            <Select
              value={take}
              onChange={handleSelection(setTake)}
              IconComponent={ExpandMoreIcon}
            >
              {takeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("dashboardPage.amountOfStreaks")}</FormHelperText>
          </StyledFormControl>
          <IconButton
            onClick={resetChart}
            aria-label={t("buttons.reset")}
            sx={{ marginRight: 1, color: "#555" }}
            size="small"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </OptionRow>
        <ActionRow>
          <GeneralButton appVariant="widget" onClick={fetchChartData}>
            {t("buttons.apply")}
          </GeneralButton>
        </ActionRow>
      </OptionContainer>

      {loading && chartData.length === 0 ? (
        <NoDataContainer>
          <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
        </NoDataContainer>
      ) : (
        <ChartContainer>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              barCategoryGap="20%"
            >
              <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="streak" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </>
  );
};

export default ChecklistAnswerStreakChart;
